<template>
  <li class="relative">
    <div
      v-if="showSpotlightLabel"
      class="lg:spotlight-label lg:inline-block bg-blue-800 text-white text-14 uppercase font-semibold rounded-t-4 lg:rounded-t-8 pl-4 pr-12 py-1 lg:px-18 lg:py-1.5"
    >
      {{ $t(`label.${groupedQuotes.label}`) }}
    </div>

    <div
      class="box-container overflow-visible flex flex-col lg:flex-row"
      :class="[
        {
          'border-b-4 border-transparent rounded-t-none lg:rounded-tr-20': showSpotlightLabel,
        },
      ]"
    >
      <div
        class="mx-4 mt-2 py-2 lg:mb-2 lg:mx-2 lg:px-2 border-b-2 border-gray-100 lg:border-0 lg:bg-gray-100 lg:rounded-4 flex justify-between lg:w-48 lg:flex-col-reverse lg:justify-end"
      >
        <div
          class="flex flex-col items-start w-full max-w-1/2 lg:flex-col-reverse lg:items-center lg:max-w-full"
        >
          <div
            v-if="groupedQuotes.bestDeal"
            class="inline-block bg-green-800 rounded-4 text-white font-semibold text-12 lg:text-14 py-0.5 px-2"
          >
            {{ $t('label.best_deal') }}
          </div>
          <BackgroundImage
            class="h-16"
            image-class="object-left py-1 lg:object-center lg:px-4"
            size="contain"
            :src="groupedQuotes.psp.logo.sm"
            :alt="groupedQuotes.psp.displayName"
          />
        </div>
        <ScoreTooltip :provider="groupedQuotes.psp" />
      </div>
      <div class="flex-1 flex flex-col xl:flex-row">
        <ProviderServices
          :grouped-quotes="groupedQuotes"
          :common-promotion="commonPromotion"
          :mid-market-rate="midMarketRate"
        />

        <div
          class="shrink-0 flex lg:flex-row-reverse items-center justify-between mx-4 mt-2 mb-4 lg:ml-2 xl:w-60 xl:flex-col xl:justify-center"
        >
          <span class="link dark lg:hidden pr-2">
            {{ $t('details') }}
          </span>

          <ProviderLink
            v-if="isRecommended"
            class="btn-sm z-10 w-1/2 lg:w-auto lg:justify-self-end lg:ml-auto xl:w-full"
            :responsive="true"
            :provider="groupedQuotes.psp"
            :promotion="commonPromotion"
            location="provider_card"
          />
          <div v-else class="hidden xl:block text-center text-14">
            <strong>{{ $t('transfer_comparison.not_recommended') }}</strong>
            <p>{{ $t('transfer_comparison.poor_provider') }}</p>
            <a
              v-if="groupedQuotes.psp.url && groupedQuotes.psp.slug === 'paypal'"
              :href="groupedQuotes.psp.url"
              class="hidden lg:inline link"
              target="_blank"
              rel="noopener"
            >
              {{ $t('read_full_review') }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showSpotlightLabel"
      class="overflow-hidden pointer-events-none absolute inset-0 rounded-12 lg:rounded-20"
    >
      <div class="h-full border-b-4 border-blue-800" />
    </div>

    <MobileDetailsPanel
      :grouped-quotes="groupedQuotes"
      :common-promotion="commonPromotion"
      :mid-market-rate="midMarketRate"
      :is-active-tab="!!isActiveTab"
    />
  </li>
</template>

<script lang="ts" setup>
import type { GroupedQuotes } from './types'
import { isRecommendedProvider } from './helpers/recommended'
import ScoreTooltip from './score-tooltip.vue'
import ProviderServices from './provider-services.vue'
import ProviderLink from './provider-link.vue'
import MobileDetailsPanel from './mobile-details-panel.vue'

const props = defineProps<{
  groupedQuotes: GroupedQuotes
  midMarketRate: number
  withLabel?: boolean
  isActiveTab?: boolean
}>()

const showSpotlightLabel = computed(() => {
  return props.withLabel && props.groupedQuotes.label
})

const commonPromotion = computed(() => {
  if (!props.groupedQuotes.services[0].promotion) {
    return null
  }

  const firstPromo = props.groupedQuotes.services[0].promotion
  const allHaveSamePromo = props.groupedQuotes.services.every(
    (s) => s.promotion && s.promotion.id === firstPromo.id,
  )
  if (allHaveSamePromo) {
    return firstPromo
  }
  return null
})

const isRecommended = computed(() => {
  return isRecommendedProvider(props.groupedQuotes.psp)
})
</script>
